// src/admin/pages/OperationsOrdersReport.js
import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../firebase';
import { collection, query, getDocs, where, orderBy, startAt, endAt, Timestamp } from 'firebase/firestore';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import AdminSidebar from '../../components/AdminSidebar';
import AdminHeader from '../../components/AdminHeader';
import * as XLSX from 'xlsx';
import { exportToPDFArabic } from '../../utils/pdfExporter';
import '../../styles/OperationsReports.css';

const OperationsOrdersReport = () => {
  // حالة البيانات
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // حالة الفلاتر
  const [dateRange, setDateRange] = useState('week');
  const [startDate, setStartDate] = useState(getStartDateFromRange('week'));
  const [endDate, setEndDate] = useState(new Date());
  const [selectedRestaurant, setSelectedRestaurant] = useState('all');
  const [selectedDriver, setSelectedDriver] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('all');
  const [orderBy_field, setOrderBy_field] = useState('createdAt');
  const [orderDirection, setOrderDirection] = useState('desc');
  const [customFilter, setCustomFilter] = useState('');
  
  // بيانات القوائم المنسدلة
  const [restaurants, setRestaurants] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [statuses] = useState([]);
  const [paymentMethods] = useState([]);
  
  // بيانات المخططات والإحصاءات
  const [chartData, setChartData] = useState([]);
  const [paymentMethodData, setPaymentMethodData] = useState([]);
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [restaurantOrderData, setRestaurantOrderData] = useState([]);
  const [driverOrderData, setDriverOrderData] = useState([]);
  const [stats, setStats] = useState({
    totalOrders: 0,
    completedOrders: 0,
    cancelledOrders: 0,
    inProgressOrders: 0,
    totalRevenue: 0,
    totalDeliveryFees: 0,
    avgOrderValue: 0,
    avgDeliveryTime: 0
  });
  
  // حالة طريقة العرض
  const [viewMode, setViewMode] = useState('list'); // list, cards, table
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // الدوال المساعدة
  const formatDate = useCallback((date) => {
    if (!date) return '';
    const options = { day: 'numeric', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('ar-EG', options);
  }, []);

  const formatTime = useCallback((date) => {
    if (!date) return '';
    return date.toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' });
  }, []);

  const formatCurrency = useCallback((amount) => {
    if (amount === undefined || amount === null) return '0.000 د.ك';
    return `${Number(amount).toFixed(3)} د.ك`;
  }, []);

  // دالة تحويل التاريخ من Firestore
  const toDateTime = useCallback((timestamp) => {
    if (!timestamp) return null;
    if (timestamp instanceof Date) return timestamp;
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    return new Date(timestamp);
  }, []);

  // دالة الحصول على تاريخ البداية من النطاق
  function getStartDateFromRange(range) {
    const now = new Date();
    const startDate = new Date();
    
    switch (range) {
      case 'today':
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'yesterday':
        startDate.setDate(now.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        startDate.setMonth(now.getMonth() - 3);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        startDate.setDate(now.getDate() - 7);
    }
    
    return startDate;
  }

  // دالة حساب الإحصائيات
  const calculateStats = useCallback((ordersData) => {
    const stats = {
      totalOrders: 0,
      completedOrders: 0,
      cancelledOrders: 0,
      totalRevenue: 0,
      totalDeliveryFees: 0,
      avgOrderValue: 0,
      avgDeliveryTime: 0,
      avgPreparationTime: 0
    };

    // حساب الإحصائيات
    ordersData.forEach(order => {
      stats.totalOrders += 1;
      
      if (order.status === 'تم التسليم ✅' || order.status?.includes('تم التسليم')) {
        stats.completedOrders += 1;
        stats.totalRevenue += order.amount || 0;
        stats.totalDeliveryFees += order.deliveryFee || 0;
      }
      
      if (order.status === 'ملغي ❌' || order.status?.includes('ملغي')) {
        stats.cancelledOrders += 1;
      }
    });

    // حساب المتوسطات
    stats.avgOrderValue = stats.completedOrders > 0 ? stats.totalRevenue / stats.completedOrders : 0;
    
    setStats(stats);
  }, []);

  // دالة إعداد بيانات المخطط
  const prepareChartData = useCallback((ordersData) => {
    const map = {};
    const today = new Date();
    
    // إنشاء نقاط البيانات للفترة المحددة
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      map[formatDate(d)] = { date: formatDate(d), orders: 0, completed: 0, cancelled: 0 };
    }
    
    // ملء البيانات
    ordersData.forEach(order => {
      const date = order.createdAtFormatted;
      if (map[date]) {
        map[date].orders += 1;
        if (order.status === 'تم التسليم ✅' || order.status?.includes('تم التسليم')) {
          map[date].completed += 1;
        }
        if (order.status === 'ملغي ❌' || order.status?.includes('ملغي')) {
          map[date].cancelled += 1;
        }
      }
    });
    
    setChartData(Object.values(map));
  }, [formatDate]);

  // دالة إعداد بيانات طرق الدفع
  const preparePaymentMethodData = useCallback((ordersData) => {
    const map = {};
    
    ordersData.forEach(order => {
      const method = order.paymentMethod || 'غير معروف';
      if (!map[method]) {
        map[method] = { name: method, value: 0 };
      }
      map[method].value += 1;
    });
    
    setPaymentMethodData(Object.values(map));
  }, []);

  // دالة إعداد بيانات حالة الطلبات
  const prepareOrderStatusData = useCallback((ordersData) => {
    const map = {};
    
    ordersData.forEach(order => {
      const status = order.status || 'غير معروف';
      if (!map[status]) {
        map[status] = { name: status, value: 0 };
      }
      map[status].value += 1;
    });
    
    setOrderStatusData(Object.values(map));
  }, []);

  // دالة إعداد بيانات طلبات المطاعم
  const prepareRestaurantOrderData = useCallback((ordersData) => {
    const map = {};
    
    ordersData.forEach(order => {
      const restaurant = order.restaurantName || 'غير معروف';
      if (!map[restaurant]) {
        map[restaurant] = { name: restaurant, orders: 0, completed: 0, cancelled: 0 };
      }
      map[restaurant].orders += 1;
      if (order.status === 'تم التسليم ✅' || order.status?.includes('تم التسليم')) {
        map[restaurant].completed += 1;
      }
      if (order.status === 'ملغي ❌' || order.status?.includes('ملغي')) {
        map[restaurant].cancelled += 1;
      }
    });
    
    setRestaurantOrderData(Object.values(map).sort((a, b) => b.orders - a.orders));
  }, []);

  // دالة إعداد بيانات طلبات السائقين
  const prepareDriverOrderData = useCallback((ordersData) => {
    const map = {};
    
    ordersData.forEach(order => {
      const driver = order.driverName || 'غير معروف';
      if (!map[driver]) {
        map[driver] = { name: driver, orders: 0, completed: 0, cancelled: 0 };
      }
      map[driver].orders += 1;
      if (order.status === 'تم التسليم ✅' || order.status?.includes('تم التسليم')) {
        map[driver].completed += 1;
      }
      if (order.status === 'ملغي ❌' || order.status?.includes('ملغي')) {
        map[driver].cancelled += 1;
      }
    });
    
    setDriverOrderData(Object.values(map).sort((a, b) => b.orders - a.orders));
  }, []);

  // تحميل البيانات من Firestore
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // جلب القوائم المنسدلة أولاً
      // المطاعم
      const restaurantsSnapshot = await getDocs(collection(db, 'restaurants'));
      const restaurantsData = restaurantsSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || 'مطعم غير معروف'
      }));
      setRestaurants(restaurantsData);
      
      // السائقين
      const driversSnapshot = await getDocs(collection(db, 'drivers'));
      const driversData = driversSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || 'سائق غير معروف'
      }));
      setDrivers(driversData);
      
      // بناء استعلام الطلبات مع الفلاتر
      let ordersQuery = collection(db, 'orders');
      let queryConstraints = [];
      
      // تحويل التواريخ إلى Timestamp
      const firestoreStartDate = Timestamp.fromDate(startDate);
      const firestoreEndDate = Timestamp.fromDate(endDate);
      
      // فلتر المطعم
      if (selectedRestaurant !== 'all') {
        queryConstraints.push(where('restaurantId', '==', selectedRestaurant));
      }
      
      // فلتر السائق
      if (selectedDriver !== 'all') {
        queryConstraints.push(where('driverId', '==', selectedDriver));
      }
      
      // فلتر الحالة
      if (selectedStatus !== 'all') {
        queryConstraints.push(where('status', '==', selectedStatus));
      }
      
      // فلتر طريقة الدفع
      if (selectedPaymentMethod !== 'all') {
        queryConstraints.push(where('paymentMethod', '==', selectedPaymentMethod));
      }
      
      // تطبيق الترتيب
      queryConstraints.push(orderBy(orderBy_field, orderDirection));
      // إضافة startAt و endAt
      queryConstraints.push(startAt(firestoreStartDate));
      queryConstraints.push(endAt(firestoreEndDate));
      
      // تنفيذ الاستعلام
      const ordersSnapshot = await getDocs(query(ordersQuery, ...queryConstraints));
      
      console.log(`تم العثور على ${ordersSnapshot.size} طلب`);
      
      // معالجة البيانات
      const ordersData = ordersSnapshot.docs.map(doc => {
        const data = doc.data();
        const createdAt = toDateTime(data.createdAt);
        const deliveredAt = toDateTime(data.deliveredAt);
        
        // حساب الإجمالي
        let total = 0;
        if (data.amount !== undefined) {
          total = parseFloat(data.amount) || 0;
        }
        
        // حساب رسوم التوصيل
        let deliveryFee = 0;
        if (data.deliveryFee !== undefined) {
          deliveryFee = parseFloat(data.deliveryFee) || 0;
        }
        
        // معالجة مصدر الطلب
        const orderSource = data.orderSource || 'غير معروف';
        
        return {
          id: doc.id,
          ...data,
          createdAt,
          deliveredAt,
          total,
          deliveryFee,
          orderSource,
          createdAtFormatted: createdAt ? formatDate(createdAt) : 'غير معروف',
          deliveredAtFormatted: deliveredAt ? formatDate(deliveredAt) : 'غير معروف',
          createdAtTime: createdAt ? formatTime(createdAt) : 'غير معروف',
          deliveredAtTime: deliveredAt ? formatTime(deliveredAt) : 'غير معروف',
          totalFormatted: formatCurrency(total),
          deliveryFeeFormatted: formatCurrency(deliveryFee)
        };
      });
      
      setOrders(ordersData);
      
      // تحديث الإحصائيات والرسوم البيانية
      calculateStats(ordersData);
      prepareChartData(ordersData);
      preparePaymentMethodData(ordersData);
      prepareOrderStatusData(ordersData);
      prepareRestaurantOrderData(ordersData);
      prepareDriverOrderData(ordersData);
      
    } catch (error) {
      console.error('خطأ في جلب البيانات:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [
    startDate,
    endDate,
    selectedRestaurant,
    selectedDriver,
    selectedStatus,
    selectedPaymentMethod,
    orderBy_field,
    orderDirection,
    toDateTime,
    formatDate,
    formatTime,
    formatCurrency,
    calculateStats,
    prepareChartData,
    preparePaymentMethodData,
    prepareOrderStatusData,
    prepareRestaurantOrderData,
    prepareDriverOrderData
  ]);

  // تطبيق الفلاتر عند تغيير نطاق التاريخ
  useEffect(() => {
    if (dateRange !== 'custom') {
      setStartDate(getStartDateFromRange(dateRange));
      setEndDate(new Date());
    }
  }, [dateRange]);

  // تحميل البيانات عند تغيير الفلاتر
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // دوال التصدير للإكسل وملفات PDF
  const exportToExcel = () => {
    try {
      // تحضير البيانات
      const excelData = orders.map(order => ({
        'رقم الطلب': order.id,
        'رقم الطلب المرجعي': order.orderCode || '',
        'تاريخ الطلب': order.createdAtFormatted,
        'وقت الطلب': order.createdAtTime,
        'اسم المطعم': order.restaurantName || 'غير معروف',
        'اسم السائق': order.driverName || 'غير معروف',
        'اسم العميل': order.customerName || 'غير معروف',
        'رقم الهاتف': order.phone || '',
        'العنوان': order.address || '',
        'حالة الطلب': order.status || 'غير معروف',
        'قيمة الطلب': order.amount?.toFixed(3) || '0.000',
        'رسوم التوصيل': order.deliveryFee?.toFixed(3) || '0.000',
        'المبلغ الإجمالي': order.totalAmount?.toFixed(3) || '0.000',
        'طريقة الدفع': order.paymentMethod || 'غير معروف',
        'تاريخ التسليم': order.deliveredAtFormatted || '',
        'وقت التسليم': order.deliveredAtTime || '',
        'مصدر الطلب': order.orderSource || 'غير معروف'
      }));
      
      // إنشاء ورقة عمل
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      
      // تعديل عرض الأعمدة
      const columnWidths = [
        { wch: 20 }, // رقم الطلب
        { wch: 15 }, // رقم الطلب المرجعي
        { wch: 15 }, // تاريخ الطلب
        { wch: 10 }, // وقت الطلب
        { wch: 20 }, // اسم المطعم
        { wch: 20 }, // اسم السائق
        { wch: 20 }, // اسم العميل
        { wch: 15 }, // رقم الهاتف
        { wch: 30 }, // العنوان
        { wch: 15 }, // حالة الطلب
        { wch: 12 }, // قيمة الطلب
        { wch: 12 }, // رسوم التوصيل
        { wch: 12 }, // المبلغ الإجمالي
        { wch: 15 }, // طريقة الدفع
        { wch: 15 }, // تاريخ التسليم
        { wch: 10 }, // وقت التسليم
        { wch: 15 }, // مصدر الطلب
      ];
      
      worksheet['!cols'] = columnWidths;
      
      // إنشاء الكتاب
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'تقرير الطلبات');
      
      // إنشاء ملف إكسل للتحميل
      const fileName = `تقرير_الطلبات_${formatDate(startDate)}_${formatDate(endDate)}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
    } catch (error) {
      console.error('خطأ في تصدير ملف Excel:', error);
      setError('حدث خطأ أثناء تصدير البيانات إلى Excel.');
    }
  };

  const exportToPDF = () => {
    try {
      // تحضير البيانات للتقرير
      const reportData = {
        title: 'تقرير الطلبات التشغيلي',
        dateRange: `${formatDate(startDate)} - ${formatDate(endDate)}`,
        stats,
        filters: {
          restaurant: selectedRestaurant !== 'all' ? 
            restaurants.find(r => r.id === selectedRestaurant)?.name || 'غير معروف' : 'جميع المطاعم',
          driver: selectedDriver !== 'all' ? 
            drivers.find(d => d.id === selectedDriver)?.name || 'غير معروف' : 'جميع السائقين',
          status: selectedStatus !== 'all' ? selectedStatus : 'جميع الحالات',
          paymentMethod: selectedPaymentMethod !== 'all' ? selectedPaymentMethod : 'جميع طرق الدفع'
        },
        orders: orders.slice(0, 50), // أخذ أول 50 طلب فقط لتجنب التقارير الكبيرة جدًا
        charts: {
          paymentMethodData,
          orderStatusData
        }
      };
      
      // استدعاء وظيفة تصدير PDF مع دعم اللغة العربية
      exportToPDFArabic({
        filename: `تقرير_الطلبات_${formatDate(startDate)}_${formatDate(endDate)}.pdf`,
        margins: { top: 20, right: 20, bottom: 20, left: 20 }
      }, reportData);
      
    } catch (error) {
      console.error('خطأ في تصدير ملف PDF:', error);
      setError('حدث خطأ أثناء تصدير البيانات إلى PDF.');
    }
  };

  // الصفحات - المعالجة
  const totalPages = Math.ceil(orders.length / itemsPerPage);
  const paginatedOrders = orders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  
  // التحقق من البيانات
  const hasData = orders.length > 0;
  
  // ألوان الرسوم البيانية
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#FF6B6B', '#4CAF50', '#FF1493', '#9370DB', '#20B2AA'];

  
  // عرض التطبيق
  return (
    <div className="operations-reports-page" dir="rtl">
      <AdminSidebar />
      <div className="operations-reports-content">
        <AdminHeader title="تقرير الطلبات التشغيلي" />
        
        {/* فلاتر التقرير */}
        <div className="report-filters-container">
          <div className="report-filters">
            <div className="filter-group">
              <label>النطاق الزمني:</label>
              <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
                <option value="today">اليوم</option>
                <option value="yesterday">الأمس</option>
                <option value="week">آخر 7 أيام</option>
                <option value="month">آخر 30 يوم</option>
                <option value="quarter">آخر 3 أشهر</option>
                <option value="year">آخر سنة</option>
                <option value="custom">مخصص</option>
              </select>
            </div>
            
            {dateRange === 'custom' && (
              <div className="date-range-inputs">
                <div className="filter-group">
                  <label>من:</label>
                  <input
                    type="date"
                    value={startDate.toISOString().split('T')[0]}
                    onChange={(e) => setStartDate(new Date(e.target.value))}
                  />
                </div>
                <div className="filter-group">
                  <label>إلى:</label>
                  <input
                    type="date"
                    value={endDate.toISOString().split('T')[0]}
                    onChange={(e) => setEndDate(new Date(e.target.value))}
                  />
                </div>
              </div>
            )}
            
            <div className="filter-group">
              <label>المطعم:</label>
              <select value={selectedRestaurant} onChange={(e) => setSelectedRestaurant(e.target.value)}>
                <option value="all">جميع المطاعم</option>
                {restaurants.map(restaurant => (
                  <option key={restaurant.id} value={restaurant.id}>{restaurant.name}</option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label>السائق:</label>
              <select value={selectedDriver} onChange={(e) => setSelectedDriver(e.target.value)}>
                <option value="all">جميع السائقين</option>
                {drivers.map(driver => (
                  <option key={driver.id} value={driver.id}>{driver.name}</option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label>الحالة:</label>
              <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                <option value="all">جميع الحالات</option>
                {statuses.map(status => (
                  <option key={status.id} value={status.id}>{status.name}</option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label>طريقة الدفع:</label>
              <select value={selectedPaymentMethod} onChange={(e) => setSelectedPaymentMethod(e.target.value)}>
                <option value="all">جميع طرق الدفع</option>
                {paymentMethods.map(method => (
                  <option key={method.id} value={method.id}>{method.name}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="toggle-advanced-filters">
            <button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}>
              {showAdvancedFilters ? 'إخفاء الفلاتر المتقدمة' : 'عرض الفلاتر المتقدمة'}
            </button>
          </div>
          
          {showAdvancedFilters && (
            <div className="advanced-filters">
              <div className="filter-group">
                <label>ترتيب حسب:</label>
                <select value={orderBy_field} onChange={(e) => setOrderBy_field(e.target.value)}>
                  <option value="createdAt">تاريخ الإنشاء</option>
                  <option value="amount">قيمة الطلب</option>
                  <option value="status">الحالة</option>
                  <option value="paymentMethod">طريقة الدفع</option>
                  <option value="deliveredAt">تاريخ التسليم</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>اتجاه الترتيب:</label>
                <select value={orderDirection} onChange={(e) => setOrderDirection(e.target.value)}>
                  <option value="desc">تنازلي</option>
                  <option value="asc">تصاعدي</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>بحث:</label>
                <input
                  type="text"
                  value={customFilter}
                  onChange={(e) => setCustomFilter(e.target.value)}
                  placeholder="اسم العميل، رقم الطلب، العنوان..."
                />
              </div>
            </div>
          )}
          
          <div className="report-actions">
            <button onClick={exportToExcel} className="export-btn excel">
              <i className="fas fa-file-excel"></i>
              تصدير Excel
            </button>
            <button onClick={exportToPDF} className="export-btn pdf">
              <i className="fas fa-file-pdf"></i>
              تصدير PDF
            </button>
          </div>
          
          <div className="view-toggle">
            <button
              className={`view-button ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <i className="fas fa-th-list"></i>
              قائمة
            </button>
            <button
              className={`view-button ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
            >
              <i className="fas fa-th-large"></i>
              بطاقات
            </button>
            <button
              className={`view-button ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
            >
              <i className="fas fa-table"></i>
              جدول
            </button>
          </div>
        </div>
        
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>جاري تحميل البيانات...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <i className="fas fa-exclamation-triangle"></i>
            <p>{error}</p>
          </div>
        ) : (
          <div className="report-content">
            {/* الإحصاءات */}
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon"><i className="fas fa-shopping-cart"></i></div>
                <div className="stat-content">
                  <h3>إجمالي الطلبات</h3>
                  <p>{stats.totalOrders}</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon completed"><i className="fas fa-check-circle"></i></div>
                <div className="stat-content">
                  <h3>الطلبات المكتملة</h3>
                  <p>{stats.completedOrders}</p>
                  <small>{stats.totalOrders > 0 ? Math.round((stats.completedOrders / stats.totalOrders) * 100) : 0}% من إجمالي الطلبات</small>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon cancelled"><i className="fas fa-times-circle"></i></div>
                <div className="stat-content">
                  <h3>الطلبات الملغاة</h3>
                  <p>{stats.cancelledOrders}</p>
                  <small>{stats.totalOrders > 0 ? Math.round((stats.cancelledOrders / stats.totalOrders) * 100) : 0}% من إجمالي الطلبات</small>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon in-progress"><i className="fas fa-spinner"></i></div>
                <div className="stat-content">
                  <h3>الطلبات قيد التنفيذ</h3>
                  <p>{stats.inProgressOrders}</p>
                  <small>{stats.totalOrders > 0 ? Math.round((stats.inProgressOrders / stats.totalOrders) * 100) : 0}% من إجمالي الطلبات</small>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon"><i className="fas fa-money-bill-wave"></i></div>
                <div className="stat-content">
                  <h3>إجمالي الإيرادات</h3>
                  <p>{formatCurrency(stats.totalRevenue)}</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon"><i className="fas fa-truck"></i></div>
                <div className="stat-content">
                  <h3>رسوم التوصيل</h3>
                  <p>{formatCurrency(stats.totalDeliveryFees)}</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon"><i className="fas fa-tags"></i></div>
                <div className="stat-content">
                  <h3>متوسط قيمة الطلب</h3>
                  <p>{formatCurrency(stats.avgOrderValue)}</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon"><i className="fas fa-clock"></i></div>
                <div className="stat-content">
                  <h3>متوسط وقت التوصيل</h3>
                  <p>{stats.avgDeliveryTime} دقيقة</p>
                </div>
              </div>
            </div>
            
            {/* المخططات */}
            <div className="charts-container">
              <div className="chart-card">
                <h3>تطور الطلبات</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip formatter={(value, name) => [
                      name === 'orders' ? `${value} طلب` : formatCurrency(value),
                      name === 'orders' ? 'عدد الطلبات' : 'الإيرادات'
                    ]} />
                    <Legend />
                    <Line type="monotone" dataKey="orders" stroke="#8884d8" name="عدد الطلبات" strokeWidth={2} activeDot={{ r: 8 }} />
                    <Line type="monotone" dataKey="revenue" stroke="#82ca9d" name="الإيرادات" strokeWidth={2} activeDot={{ r: 8 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              
              <div className="charts-grid">
                <div className="chart-card">
                  <h3>توزيع طرق الدفع</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={paymentMethodData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {paymentMethodData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name, props) => [
                        `${value} طلب`,
                        props.payload.name
                      ]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="chart-card">
                  <h3>توزيع حالات الطلبات</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={orderStatusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {orderStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name, props) => [
                        `${value} طلب`,
                        props.payload.name
                      ]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
              
              <div className="chart-card">
                <h3>أفضل المطاعم من حيث الطلبات</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={restaurantOrderData.slice(0, 10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={150} />
                    <Tooltip formatter={(value, name) => [
                      name === 'orders' ? `${value} طلب` : 
                      name === 'revenue' ? formatCurrency(value) : value,
                      name === 'orders' ? 'الطلبات' : 
                      name === 'revenue' ? 'الإيرادات' : name
                    ]} />
                    <Legend />
                    <Bar dataKey="orders" name="إجمالي الطلبات" fill="#8884d8" />
                    <Bar dataKey="completedOrders" name="الطلبات المكتملة" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              <div className="chart-card">
                <h3>أفضل السائقين من حيث الطلبات</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={driverOrderData.slice(0, 10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={150} />
                    <Tooltip formatter={(value, name) => [
                      name === 'orders' ? `${value} طلب` : 
                      name === 'avgDeliveryTime' ? `${value} دقيقة` : value,
                      name === 'orders' ? 'الطلبات' : 
                      name === 'avgDeliveryTime' ? 'متوسط وقت التوصيل' : name
                    ]} />
                    <Legend />
                    <Bar dataKey="orders" name="إجمالي الطلبات" fill="#8884d8" />
                    <Bar dataKey="completedOrders" name="الطلبات المكتملة" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* عرض قائمة الطلبات */}
            <div className="orders-data-section">
              <h2>قائمة الطلبات ({orders.length})</h2>
              
              {!hasData ? (
                <div className="no-data-message">
                  <i className="fas fa-info-circle"></i>
                  <p>لا توجد بيانات متاحة للفترة والفلاتر المحددة.</p>
                </div>
              ) : viewMode === 'table' ? (
                <div className="orders-table-container">
                  <table className="orders-table">
                    <thead>
                      <tr>
                        <th>رقم الطلب</th>
                        <th>التاريخ</th>
                        <th>المطعم</th>
                        <th>السائق</th>
                        <th>العميل</th>
                        <th>المبلغ</th>
                        <th>رسوم التوصيل</th>
                        <th>طريقة الدفع</th>
                        <th>الحالة</th>
                        <th>الإجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedOrders.map(order => (
                        <tr key={order.id}>
                          <td>{order.orderCode || order.id.substring(0, 8)}</td>
                          <td>{order.createdAtFormatted}</td>
                          <td>{order.restaurantName || 'غير معروف'}</td>
                          <td>{order.driverName || 'غير معروف'}</td>
                          <td>{order.customerName || 'غير معروف'}</td>
                          <td>{formatCurrency(order.amount)}</td>
                          <td>{formatCurrency(order.deliveryFee)}</td>
                          <td>{order.paymentMethod}</td>
                          <td>
                            <span className={`status-badge ${
                              order.status?.includes('تم التسليم') ? 'completed' : 
                              order.status?.includes('ملغي') ? 'cancelled' : 'in-progress'
                            }`}>
                              {order.status}
                            </span>
                          </td>
                          <td>
                            <div className="table-actions">
                              <button className="action-btn view">
                                <i className="fas fa-eye"></i>
                              </button>
                              <button className="action-btn track">
                                <i className="fas fa-map-marker-alt"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : viewMode === 'cards' ? (
                <div className="order-cards-grid">
                  {paginatedOrders.map(order => (
                    <div key={order.id} className="order-card">
                      <div className="order-card-header">
                        <span className="order-id">{order.orderCode || order.id.substring(0, 8)}</span>
                        <span className={`order-status ${
                          order.status?.includes('تم التسليم') ? 'completed' : 
                          order.status?.includes('ملغي') ? 'cancelled' : 'in-progress'
                        }`}>
                          {order.status}
                        </span>
                      </div>
                      <div className="order-card-body">
                        <div className="order-info">
                          <div className="info-row">
                            <span className="info-label">المطعم:</span>
                            <span className="info-value">{order.restaurantName || 'غير معروف'}</span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">العميل:</span>
                            <span className="info-value">{order.customerName || 'غير معروف'}</span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">السائق:</span>
                            <span className="info-value">{order.driverName || 'غير معروف'}</span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">التاريخ:</span>
                            <span className="info-value">{order.createdAtFormatted}</span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">المبلغ:</span>
                            <span className="info-value">{formatCurrency(order.totalAmount)}</span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">طريقة الدفع:</span>
                            <span className="info-value">{order.paymentMethod}</span>
                          </div>
                        </div>
                      </div>
                      <div className="order-card-footer">
                        <button className="card-btn view">
                          <i className="fas fa-eye"></i>
                          عرض
                        </button>
                        <button className="card-btn track">
                          <i className="fas fa-map-marker-alt"></i>
                          تتبع
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="orders-list">
                  {paginatedOrders.map(order => (
                    <div key={order.id} className="order-list-item">
                      <div className="order-list-header">
                        <span className="order-id">{order.orderCode || order.id.substring(0, 8)}</span>
                        <span className="order-date">{order.createdAtFormatted} - {order.createdAtTime}</span>
                        <span className={`order-status ${
                          order.status?.includes('تم التسليم') ? 'completed' : 
                          order.status?.includes('ملغي') ? 'cancelled' : 'in-progress'
                        }`}>
                          {order.status}
                        </span>
                      </div>
                      <div className="order-list-body">
                        <div className="order-list-section">
                          <div className="order-list-section-header">
                            <i className="fas fa-store"></i>
                            <span>المطعم</span>
                          </div>
                          <div className="order-list-section-content">
                            {order.restaurantName || 'غير معروف'}
                          </div>
                        </div>
                        <div className="order-list-section">
                          <div className="order-list-section-header">
                            <i className="fas fa-user"></i>
                            <span>العميل</span>
                          </div>
                          <div className="order-list-section-content">
                            {order.customerName || 'غير معروف'}
                            {order.phone && <div className="order-phone">{order.phone}</div>}
                          </div>
                        </div>
                        <div className="order-list-section">
                          <div className="order-list-section-header">
                            <i className="fas fa-motorcycle"></i>
                            <span>السائق</span>
                          </div>
                          <div className="order-list-section-content">
                            {order.driverName || 'غير معروف'}
                          </div>
                        </div>
                        <div className="order-list-section">
                          <div className="order-list-section-header">
                            <i className="fas fa-money-bill"></i>
                            <span>المبلغ</span>
                          </div>
                          <div className="order-list-section-content">
                            <div className="order-amount-details">
                              <div className="amount-row">
                                <span>الطلب:</span>
                                <span>{formatCurrency(order.amount)}</span>
                              </div>
                              <div className="amount-row">
                                <span>التوصيل:</span>
                                <span>{formatCurrency(order.deliveryFee)}</span>
                              </div>
                              <div className="amount-row total">
                                <span>الإجمالي:</span>
                                <span>{formatCurrency(order.totalAmount)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="order-list-section">
                          <div className="order-list-section-header">
                            <i className="fas fa-credit-card"></i>
                            <span>الدفع</span>
                          </div>
                          <div className="order-list-section-content">
                            {order.paymentMethod}
                          </div>
                        </div>
                      </div>
                      <div className="order-list-footer">
                        <button className="list-btn view">
                          <i className="fas fa-eye"></i>
                          عرض التفاصيل
                        </button>
                        <button className="list-btn track">
                          <i className="fas fa-map-marker-alt"></i>
                          تتبع الطلب
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* رقم الصفحة */}
              {hasData && (
                <div className="pagination">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    <i className="fas fa-chevron-right"></i>
                    السابق
                  </button>
                  
                  <div className="pagination-info">
                    صفحة {currentPage} من {totalPages}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    التالي
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  
                  <div className="items-per-page">
                    <select
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    >
                      <option value={10}>10 لكل صفحة</option>
                      <option value={20}>20 لكل صفحة</option>
                      <option value={50}>50 لكل صفحة</option>
                      <option value={100}>100 لكل صفحة</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OperationsOrdersReport;